import React, { useMemo } from 'react'
import { Collapse, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useStore } from 'effector-react'
import { useNavigate } from 'react-router-dom'
import { $inProcess, getInProgress } from '../list/model'
import { LocalBalance } from '../../../../views/widgets/walletCard/types'
import { getFinsystemNumberFormat } from '../../../../libs/utils'
import { path } from '../../../../libs'
import { useViewportMode } from '../useIsCompact'
import styles from './styles.module.css'

type InProcessRow = {
  key: string
  totalSumUsd: number
  localBalances: LocalBalance[]
}

export const InProcessTable: React.FC = () => {
  const navigate = useNavigate()
  const inProcess = useStore($inProcess)
  const loading = useStore(getInProgress.pending)
  const { isCompact, isWide } = useViewportMode()
  const totalSumWidth = isCompact ? 400 : isWide ? 560 : 460
  const scrollX = isCompact ? 1060 : isWide ? 1480 : 1280

  const dataSource: InProcessRow[] = useMemo(() => {
    const total = inProcess.localBalances.reduce(
      (acc, item) => acc + item.sumUSD,
      0,
    )
    return [
      {
        key: 'inProcess',
        totalSumUsd: total,
        localBalances: inProcess.localBalances,
      },
    ]
  }, [inProcess])

  const columns: ColumnsType<InProcessRow> = useMemo(
    () => [
      {
        title: <span className={styles.header}>Total sum</span>,
        dataIndex: 'totalSumUsd',
        key: 'totalSumUsd',
        width: totalSumWidth,
        render: (value: number) => (
          <span className={styles.total}>
            {getFinsystemNumberFormat(value, 2)}{' '}
            <span className={`${styles.total} ${styles.muted}`}>USD</span>
          </span>
        ),
      },
      {
        title: <span className={styles.header}>Currencies</span>,
        key: 'currencies',
        width: 140,
        render: (_, record) => (
          <div className={styles.bodyList}>
            {record.localBalances.map((lb) => (
              <span key={lb.currency} className={styles.body}>
                {lb.currency}
              </span>
            ))}
          </div>
        ),
      },
      {
        title: <span className={styles.header}>Local balance</span>,
        key: 'localBalance',
        render: (_, record) => (
          <div className={styles.bodyListSpread}>
            {record.localBalances.map((lb) => (
              <span key={lb.currency} className={styles.body}>
                {getFinsystemNumberFormat(lb.sum, 8)}{' '}
                <span className={`${styles.body} ${styles.muted}`}>
                  {lb.currency}
                </span>
              </span>
            ))}
          </div>
        ),
      },
    ],
    [totalSumWidth],
  )

  return (
    <Collapse
      expandIconPosition="end"
      defaultActiveKey={['inProcess']}
      className={styles.collapse}
      items={[
        {
          key: 'inProcess',
          label: <span className={styles.title}>In progress</span>,
          children: (
            <div className={styles.tableWrapper}>
              <Table<InProcessRow>
                columns={columns}
                dataSource={dataSource}
                rowKey="key"
                scroll={{ x: scrollX }}
                loading={loading}
                pagination={false}
                showHeader={true}
                onRow={() => ({
                  onClick: () =>
                    navigate(path.finSystem.wallets.inProgressDetails()),
                  style: { cursor: 'pointer' },
                })}
              />
            </div>
          ),
        },
      ]}
    />
  )
}
